import React from 'react';
import { observer } from 'mobx-react';
import { Table } from 'antd';
import { ColumnProps } from 'antd/es/table';
import {
    LoyaltyBanner,
    LoyaltyBannerSearchParams,
} from '../../stores/loyalty-banner';
import { useAuthStore, useLoyaltyBannerStore } from '../../contexts/mobx';
import { useSearchParams } from '../../utils/use-search-params';
import { useNavigation } from '../../utils/use-navigation';
import { getItemProp } from '../../utils';
import { CreateButton } from '../../components/action-buttons';
import { PaginationBar } from '../../components/pagination/pagination-bar';
import { PageTopBar } from '../../components/page-top-bar';
import { SearchInput } from '../../components/search-input';
import { Tag } from '../../components/tag';
import style from './style.module.scss';

const APP_TARGET_ICONS: Record<string, { icon: string; label: string }> = {
    QC: { icon: '/ct-logo-xl.svg', label: 'Couche-Tard' },
    CA: { icon: '/circle-k-xl-logo.svg', label: 'Circle K' },
};

const formatReadableDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-CA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
};

const renderImage = (_: unknown, record: LoyaltyBanner) => {
    const image =
        getItemProp(record.translations, 'en', 'image') ??
        getItemProp(record.translations, 'fr', 'image');
    if (!image) return null;
    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <img src={image as string} alt="" className={style.thumbnail} />
        </div>
    );
};

const renderTitle = (_: unknown, record: LoyaltyBanner) => {
    return (
        getItemProp(record.translations, 'en', 'title') ??
        getItemProp(record.translations, 'fr', 'title') ??
        null
    );
};

const renderSubtitle = (_: unknown, record: LoyaltyBanner) => {
    return (
        getItemProp(record.translations, 'en', 'subtitle') ??
        getItemProp(record.translations, 'fr', 'subtitle') ??
        null
    );
};

const renderIsActive = (_: unknown, record: LoyaltyBanner) => {
    return record.isActive ? (
        <Tag variant="success">Active</Tag>
    ) : (
        <Tag variant="error">Inactive</Tag>
    );
};

const renderAppTarget = (_: unknown, record: LoyaltyBanner) => {
    const target = record.appTarget;
    if (!target) {
        return (
            <div className={style.app_target_icons_wrapper}>
                <img
                    src="/ct-logo-xl.svg"
                    alt="Couche-Tard"
                    title="Couche-Tard"
                    className={style.app_target_icon}
                />
                <img
                    src="/circle-k-xl-logo.svg"
                    alt="Circle K"
                    title="Circle K"
                    className={style.app_target_icon}
                />
            </div>
        );
    }
    const info = APP_TARGET_ICONS[target];
    if (!info) return target;
    return (
        <img
            src={info.icon}
            alt={info.label}
            title={info.label}
            style={{ width: 16, height: 16 }}
        />
    );
};

const renderStartDate = (_: unknown, record: LoyaltyBanner) => {
    return record.startDate ? formatReadableDate(record.startDate) : '';
};

const renderEndDate = (_: unknown, record: LoyaltyBanner) => {
    return record.endDate ? formatReadableDate(record.endDate) : '';
};

export const LoyaltyBannersPage = observer(() => {
    const loyaltyBannerStore = useLoyaltyBannerStore();
    const navigation = useNavigation();
    const authStore = useAuthStore();

    const { hasManagementAccess } = authStore;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }
    const search = useSearchParams<LoyaltyBannerSearchParams>();

    React.useEffect(() => {
        loyaltyBannerStore.fetchPaginatedLoyaltyBanners(search.params);
    }, [loyaltyBannerStore, search.params]);

    const columns: ColumnProps<LoyaltyBanner>[] = [
        {
            title: 'Image',
            key: 'image',
            width: 100,
            render: renderImage,
        },
        {
            title: 'Title',
            key: 'title',
            width: 250,
            render: renderTitle,
        },
        {
            title: 'Subtitle',
            key: 'subtitle',
            width: 200,
            render: renderSubtitle,
        },
        {
            title: 'Status',
            key: 'is_active',
            render: renderIsActive,
            width: 100,
        },
        {
            title: 'App Target',
            key: 'app_target',
            render: renderAppTarget,
            width: 80,
            align: 'center',
        },
        {
            title: 'Start',
            key: 'start_date',
            render: renderStartDate,
            width: 170,
        },
        {
            title: 'End',
            key: 'end_date',
            render: renderEndDate,
            width: 170,
        },
    ];

    const goToEditPage = (o: LoyaltyBanner) => () => {
        navigation.goToLoyaltyBannerEdit(o.id);
    };

    return (
        <>
            <PageTopBar title="Loyalty Banners">
                <CreateButton
                    onClick={() => navigation.goToLoyaltyBannerCreate()}
                />
            </PageTopBar>

            <div className="action_zone_above_table">
                <div className="table_header_options_bar">
                    <div className={'query_box'}>
                        <SearchInput
                            searchValue={search.params.q}
                            onChange={(e) =>
                                search.setParams({ q: e.target.value, page: 0 })
                            }
                            allowClear={true}
                            placeholder="Search"
                        />
                    </div>
                </div>
                <PaginationBar
                    pagination={{
                        page: search.params.page,
                        pageSize: search.params.perPage,
                        total:
                            loyaltyBannerStore.paginatedLoyaltyBanners?.total,
                        onPageChange: (page) => search.setParams({ page }),
                        onPageSizeChange: (perPage) =>
                            search.setParams({ perPage, page: 0 }),
                    }}
                />
            </div>

            <Table<LoyaltyBanner>
                className={`${style.table} clickable_table_body_rows`}
                rowKey="id"
                dataSource={loyaltyBannerStore.paginatedLoyaltyBanners?.results}
                columns={columns}
                loading={loyaltyBannerStore.isFetchingPaginatedCount > 0}
                pagination={false}
                onRow={(r) => ({
                    onClick: goToEditPage(r),
                })}
                size="small"
                bordered
            />
        </>
    );
});
